import { Icon } from "@iconify/react/dist/iconify.js";
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Modal, Nav, Tab } from "react-bootstrap";
import { handleError, handleSuccess } from "../CommonJquery/CommonJquery";
import MasterLayout from "../masterLayout/MasterLayout";
import {
  delete_threads_post,
  get_threads_account,
  get_threads_auth_url,
  get_threads_demographics,
  get_threads_insights,
  get_threads_posts,
  get_threads_quota,
  get_threads_replies,
  hide_threads_reply,
  publish_threads_chain,
  publish_threads_post,
  reply_threads_post,
  save_threads_account,
  search_threads_posts,
  server_post_data,
  threads_oauth_callback,
  upload_public_media,
} from "../ServiceConnection/serviceconnection";
import Loader from "./Loader";
import "../components/CSS/gmb-premium.css";
import "../components/CSS/facebook-premium.css";
import "../components/CSS/threads-premium.css";

/* ---------------------------------------------------------------------------
   Static config
--------------------------------------------------------------------------- */

const MAX_CHARS = 500;

const SOCIAL_PROFILES = [
  { key: "google", label: "Google Business", icon: "mdi:google", ready: false },
  { key: "facebook", label: "Facebook", icon: "mdi:facebook", ready: false },
  { key: "instagram", label: "Instagram", icon: "mdi:instagram", ready: false },
  { key: "threads", label: "Threads", icon: "ri:threads-fill", ready: true },
  { key: "twitter", label: "Twitter", icon: "ri:twitter-x-fill", ready: false },
  { key: "linkedin", label: "LinkedIn", icon: "mdi:linkedin", ready: false },
];

const CHANNEL_TABS = [
  { key: "threads", label: "Threads", ready: true },
  { key: "instagram", label: "Instagram", ready: false },
  { key: "facebook", label: "Facebook", ready: false },
  { key: "twitter", label: "Twitter", ready: false },
  { key: "linkedin", label: "LinkedIn", ready: false },
  { key: "google", label: "Google", ready: false },
];

const POST_TYPES = [
  { key: "TEXT", label: "Text", icon: "mdi:text" },
  { key: "IMAGE", label: "Image", icon: "mdi:image-outline" },
  { key: "VIDEO", label: "Video", icon: "mdi:video-outline" },
  { key: "CAROUSEL", label: "Carousel", icon: "mdi:image-multiple-outline" },
  { key: "CHAIN", label: "Chain", icon: "mdi:format-list-numbered" },
];

const REPLY_AUDIENCE = [
  {
    key: "everyone",
    label: "Anyone",
    hint: "Anyone on Threads can reply",
    icon: "mdi:earth",
  },
  {
    key: "accounts_you_follow",
    label: "Profiles you follow",
    hint: "Only accounts you follow can reply",
    icon: "mdi:account-heart-outline",
  },
  {
    key: "mentioned_only",
    label: "Mentioned only",
    hint: "Only profiles you mention can reply",
    icon: "mdi:at",
  },
];

const TIPS = [
  {
    title: "Start A Conversation",
    body: "Threads ranks replies heavily — a post that asks something specific outperforms a polished announcement.",
  },
  {
    title: "Chain Instead Of Cramming",
    body: "You get 500 characters, but shorter posts chained together keep people reading and each link gets its own reach.",
  },
  {
    title: "Reply To Your Repliers",
    body: "Staying active under your own post keeps it circulating far longer than posting and walking away.",
  },
];

const emptyPost = {
  post_type: "TEXT",
  text: "",
  link_attachment: "",
  alt_text: "",
  reply_control: "everyone",
};

const todayISO = () => new Date().toISOString().slice(0, 10);
const daysAgoISO = (n) =>
  new Date(Date.now() - n * 86400000).toISOString().slice(0, 10);

const DEMO_SECTIONS = [
  { key: "country", label: "By Country" },
  { key: "city", label: "By City" },
  { key: "age", label: "By Age" },
  { key: "gender", label: "By Gender" },
];

/* ---------------------------------------------------------------------------
   Character counter
--------------------------------------------------------------------------- */

function CharBar({ count, max = MAX_CHARS }) {
  const remaining = max - count;
  const level = remaining < 0 ? "over" : remaining <= 50 ? "warn" : "";
  const pct = Math.min((count / max) * 100, 100);

  return (
    <div className="th-counter">
      <span className="th-counter-track">
        <span
          className={`th-counter-fill ${level}`}
          style={{ width: `${pct}%` }}
        />
      </span>
      <span className={`th-counter-value ${level}`}>{remaining} left</span>
    </div>
  );
}

/* ------------------------------------------------------------------------ */

function ThreadsPanel() {
  const [loading, setLoading] = useState(false);
  const [loadingNote, setLoadingNote] = useState("");
  const [activeTab, setActiveTab] = useState("account");

  // account
  const [account, setAccount] = useState(null);
  const [quota, setQuota] = useState(null);

  // posts
  const [posts, setPosts] = useState([]);
  const [confirmDelete, setConfirmDelete] = useState(null);

  // composer
  const [showComposer, setShowComposer] = useState(false);
  const [form, setForm] = useState(emptyPost);
  const [media, setMedia] = useState([]); // [{ url, preview, kind }]
  const [chainItems, setChainItems] = useState([
    { text: "", media: null },
    { text: "", media: null },
  ]);
  const [channel, setChannel] = useState("threads");

  // replies
  const [showReplies, setShowReplies] = useState(false);
  const [replyPost, setReplyPost] = useState(null);
  const [replies, setReplies] = useState([]);
  const [replyBox, setReplyBox] = useState({});

  // insights
  const [range, setRange] = useState({
    start_date: daysAgoISO(29),
    end_date: todayISO(),
  });
  const [insights, setInsights] = useState(null);
  const [demographics, setDemographics] = useState(null);

  // search
  const [query, setQuery] = useState("");
  const [searchType, setSearchType] = useState("TOP");
  const [searchResults, setSearchResults] = useState(null);

  const clickRef = useRef(0);

  /* ------------------------- common caller ------------------------- */
  const call = useCallback(async (url, fields = {}, files = {}) => {
    const fd = new FormData();
    Object.entries(fields).forEach(([k, v]) => {
      if (v !== undefined && v !== null && v !== "") fd.append(k, v);
    });
    Object.entries(files).forEach(([k, f]) => f && fd.append(k, f));

    try {
      const res = await server_post_data(url, fd);
      const parts = res.message.split("~@~");
      if (parseInt(parts[0]) === 1) {
        handleError(parts[1]);
        return null;
      }
      const parsed = JSON.parse(parts[1]);
      if (parsed.error) {
        handleError(parsed.message || "Threads API error");
        return { _error: parsed };
      }
      return parsed;
    } catch (e) {
      handleError("network");
      return null;
    }
  }, []);

  /* ------------------------- loaders ------------------------- */
  const loadAccount = useCallback(async () => {
    setLoading(true);
    const d = await call(get_threads_account);
    if (d?.account) setAccount(d.account);
    setLoading(false);
  }, [call]);

  const loadQuota = useCallback(async () => {
    const d = await call(get_threads_quota);
    if (d && !d._error) setQuota(d);
  }, [call]);

  const loadPosts = useCallback(async () => {
    setLoading(true);
    setLoadingNote("Loading posts and engagement...");
    const d = await call(get_threads_posts, { limit: 25 });
    setPosts(d?.posts || []);
    setLoading(false);
    setLoadingNote("");
  }, [call]);

  const loadInsights = useCallback(async () => {
    setLoading(true);
    const d = await call(get_threads_insights, range);
    setInsights(d && !d._error ? d.insights : null);
    setLoading(false);
  }, [call, range]);

  const loadDemographics = useCallback(async () => {
    setLoading(true);
    const d = await call(get_threads_demographics);
    setDemographics(d && !d._error ? d : null);
    setLoading(false);
  }, [call]);

  useEffect(() => {
    loadAccount();
  }, [loadAccount]);

  useEffect(() => {
    if (activeTab === "posts") loadPosts();
    if (activeTab === "insights") loadInsights();
    if (activeTab === "audience") loadDemographics();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);

  /* ------------------------- OAuth popup flow ------------------------- */
  const exchangeCode = useCallback(
    async (code, state) => {
      setLoading(true);
      const d = await call(threads_oauth_callback, { code, state });
      setLoading(false);
      if (!d?.account) return;

      setLoading(true);
      const saved = await call(save_threads_account, {
        user_id: d.account.userId,
        username: d.account.username,
        access_token: d.accessToken,
        expires_in: d.expiresIn,
      });
      setLoading(false);

      if (saved) {
        handleSuccess(`Connected as @${d.account.username}`);
        loadAccount();
      }
    },
    [call, loadAccount],
  );

  // Threads runs its own OAuth on threads.net — no browser SDK, so the popup
  // posts the auth code back here.
  useEffect(() => {
    const onMessage = (event) => {
      if (event.origin !== window.location.origin) return;
      const data = event.data || {};
      if (data.source !== "threads-oauth") return;

      if (data.error) {
        handleError(data.error);
        return;
      }
      if (data.code) exchangeCode(data.code, data.state);
    };

    window.addEventListener("message", onMessage);
    return () => window.removeEventListener("message", onMessage);
  }, [exchangeCode]);

  const handleConnect = async () => {
    const d = await call(get_threads_auth_url);
    if (!d?.authUrl) return;

    const w = 600;
    const h = 740;
    const left = window.screenX + (window.outerWidth - w) / 2;
    const top = window.screenY + (window.outerHeight - h) / 2;

    const popup = window.open(
      d.authUrl,
      "threads-oauth",
      `width=${w},height=${h},left=${left},top=${top}`,
    );
    if (!popup) handleError("Please allow popups to connect Threads");
  };

  /* ------------------------- media ------------------------- */
  // Threads can only fetch media from a public URL, so files go to our own
  // storage first and the returned URL is what gets published.
  const uploadToPublicUrl = async (file) => {
    setLoadingNote("Uploading media...");
    setLoading(true);
    const d = await call(upload_public_media, {}, { media_file: file });
    setLoading(false);
    setLoadingNote("");
    if (!d?.url) return null;
    return {
      url: d.url,
      kind: d.mediaKind,
      preview: URL.createObjectURL(file),
    };
  };

  const onPickMedia = async (file) => {
    if (!file) return;
    const isCarousel = form.post_type === "CAROUSEL";

    if (isCarousel && media.length >= 20) {
      handleError("A carousel can hold at most 20 items");
      return;
    }

    const m = await uploadToPublicUrl(file);
    if (!m) return;

    setMedia((s) => (isCarousel ? [...s, m] : [m]));
  };

  const onPickChainMedia = async (idx, file) => {
    if (!file) return;
    const m = await uploadToPublicUrl(file);
    if (!m) return;
    setChainItems((s) =>
      s.map((it, i) => (i === idx ? { ...it, media: m } : it)),
    );
  };

  /* ------------------------- publish ------------------------- */
  const resetComposer = () => {
    setForm(emptyPost);
    setMedia([]);
    setChainItems([
      { text: "", media: null },
      { text: "", media: null },
    ]);
    setChannel("threads");
  };

  const openComposer = () => {
    resetComposer();
    loadQuota();
    setShowComposer(true);
  };

  const mainCount = useMemo(() => form.text.length, [form.text]);

  const publish = async () => {
    const t = form.post_type;

    if (t === "CHAIN") {
      const filled = chainItems.filter((it) => it.text.trim() || it.media);
      if (filled.length < 2) {
        handleError("A chain needs at least 2 posts");
        return;
      }
      const overIdx = filled.findIndex((it) => it.text.length > MAX_CHARS);
      if (overIdx !== -1) {
        handleError(`Post ${overIdx + 1} exceeds ${MAX_CHARS} characters`);
        return;
      }
    } else {
      if (mainCount > MAX_CHARS) {
        handleError(`Post cannot exceed ${MAX_CHARS} characters`);
        return;
      }
      if (t === "TEXT" && !form.text.trim()) {
        handleError("Post text is required");
        return;
      }
      if (t === "CAROUSEL" && media.length < 2) {
        handleError("A carousel needs at least 2 items");
        return;
      }
      if ((t === "IMAGE" || t === "VIDEO") && media.length === 0) {
        handleError("Please add media first");
        return;
      }
    }

    if (clickRef.current === 1) return;
    clickRef.current = 1;

    const hasMedia = t !== "TEXT" && t !== "CHAIN";
    setLoadingNote(
      hasMedia
        ? "Threads is processing your media — this can take up to a minute."
        : t === "CHAIN"
          ? "Publishing your chain..."
          : "Publishing to Threads...",
    );
    setLoading(true);

    let d;
    if (t === "CHAIN") {
      d = await call(publish_threads_chain, {
        chain: JSON.stringify(
          chainItems
            .filter((it) => it.text.trim() || it.media)
            .map((it) => ({
              text: it.text,
              type: it.media ? it.media.kind : "TEXT",
              url: it.media ? it.media.url : "",
            })),
        ),
        reply_control: form.reply_control,
      });
    } else {
      const payload = {
        post_type: t,
        text: form.text,
        reply_control: form.reply_control,
      };

      if (t === "CAROUSEL") {
        payload.children = JSON.stringify(
          media.map((m) => ({ type: m.kind, url: m.url })),
        );
      } else if (t === "VIDEO") {
        payload.video_url = media[0]?.url;
        payload.alt_text = form.alt_text;
      } else if (t === "IMAGE") {
        payload.image_url = media[0]?.url;
        payload.alt_text = form.alt_text;
      } else if (form.link_attachment) {
        payload.link_attachment = form.link_attachment;
      }

      d = await call(publish_threads_post, payload);
    }

    setLoading(false);
    setLoadingNote("");
    clickRef.current = 0;

    if (d && !d._error) {
      handleSuccess(
        d.postType === "CHAIN"
          ? `Chain published (${d.totalCount} posts)`
          : "Published to Threads successfully",
      );
      setShowComposer(false);
      loadPosts();
      loadQuota();
    } else if (d?._error?.postedPosts?.length) {
      // Partial chain failure — the earlier posts are live, so refresh
      // rather than leaving the list stale.
      loadPosts();
    }
  };

  const removePost = async () => {
    setLoading(true);
    const d = await call(delete_threads_post, { post_id: confirmDelete });
    setLoading(false);
    setConfirmDelete(null);
    if (d && !d._error) {
      handleSuccess("Post deleted successfully");
      loadPosts();
    }
  };

  /* ------------------------- replies ------------------------- */
  const openReplies = async (p) => {
    setReplyPost(p);
    setShowReplies(true);
    setLoading(true);
    const d = await call(get_threads_replies, {
      post_id: p.postId,
      view: "conversation",
      limit: 50,
    });
    setReplies(d && !d._error ? d.replies : []);
    setLoading(false);
  };

  const sendReply = async (targetId) => {
    const msg = (replyBox[targetId] || "").trim();
    if (!msg) return;
    if (msg.length > MAX_CHARS) {
      handleError(`Reply cannot exceed ${MAX_CHARS} characters`);
      return;
    }
    setLoading(true);
    const d = await call(reply_threads_post, {
      reply_to_id: targetId,
      text: msg,
    });
    setLoading(false);
    if (d && !d._error) {
      handleSuccess("Reply posted successfully");
      setReplyBox((s) => ({ ...s, [targetId]: "" }));
      openReplies(replyPost);
    }
  };

  const toggleHide = async (r) => {
    setLoading(true);
    const d = await call(hide_threads_reply, {
      reply_id: r.replyId,
      hide: r.hideStatus === "HIDDEN" ? "0" : "1",
    });
    setLoading(false);
    if (d && !d._error) {
      handleSuccess(d.message || "Updated successfully");
      openReplies(replyPost);
    }
  };

  /* ------------------------- search ------------------------- */
  const runSearch = async () => {
    if (!query.trim()) {
      handleError("Please enter a search term");
      return;
    }
    setLoading(true);
    const d = await call(search_threads_posts, {
      query: query.trim(),
      search_type: searchType,
      limit: 30,
    });
    setLoading(false);
    if (d && !d._error) setSearchResults(d);
  };

  /* ------------------------- helpers ------------------------- */
  const initials = (n) => (n || "?").trim().charAt(0).toUpperCase();

  const fmtDate = (d) =>
    d
      ? new Date(d).toLocaleDateString("en-IN", {
          day: "2-digit",
          month: "short",
          year: "numeric",
        })
      : "-";

  const compact = (n) => {
    if (n === undefined || n === null) return 0;
    if (n >= 1000000) return `${(n / 1000000).toFixed(1)}M`;
    if (n >= 1000) return `${(n / 1000).toFixed(1)}K`;
    return n;
  };

  const isChain = form.post_type === "CHAIN";
  const isCarousel = form.post_type === "CAROUSEL";
  const isMedia = ["IMAGE", "VIDEO", "CAROUSEL"].includes(form.post_type);

  const previewItems = isChain
    ? chainItems.filter((it) => it.text.trim() || it.media)
    : [{ text: form.text, media: media[0] || null }];

  return (
    <MasterLayout>
      <div className="gmb-premium fb-premium th-premium">
        {loading && <Loader />}

        {/* ------------------------- Topbar ------------------------- */}
        <div className="gmb-topbar">
          <div className="gmb-topbar-title">
            <h5 className="gmb-display">Threads</h5>
            <span className="gmb-royal-badge">
              <Icon icon="ri:threads-fill" /> Social Suite
            </span>
            {account && (
              <span className="gmb-live">
                <span className="gmb-live-dot" /> Live
              </span>
            )}
          </div>
          <div className="gmb-topbar-actions">
            <button
              className="btn btn-outline-primary-600"
              onClick={handleConnect}
            >
              <Icon icon="mdi:account-switch-outline" /> Switch Account
            </button>
            <button className="btn btn-primary-600" onClick={openComposer}>
              <Icon icon="ic:baseline-plus" /> Create Post
            </button>
          </div>
        </div>

        {/* ------------------------- Account hero ------------------------- */}
        {account && (
          <div className="fb-page-hero">
            <img
              src={account.profileImage || ""}
              alt={account.username}
              onError={(e) => (e.currentTarget.style.visibility = "hidden")}
            />
            <div>
              <h6>{account.name || account.username}</h6>
              <span>@{account.username}</span>
            </div>
            <div className="fb-page-stats">
              <div>
                <b className="gmb-mono">{compact(account.followerCount)}</b>
                <small>Followers</small>
              </div>
              <div>
                <b className="gmb-mono">{posts.length}</b>
                <small>Posts Loaded</small>
              </div>
              <div>
                <b className="gmb-mono">{quota ? quota.postsRemaining : "—"}</b>
                <small>Posts Left Today</small>
              </div>
            </div>
          </div>
        )}

        {/* ------------------------- Tabs ------------------------- */}
        <Tab.Container activeKey={activeTab} onSelect={(k) => setActiveTab(k)}>
          <Nav className="nav-tabs">
            <Nav.Item>
              <Nav.Link eventKey="account">
                <Icon icon="mdi:information-outline" /> Profile
              </Nav.Link>
            </Nav.Item>
            <Nav.Item>
              <Nav.Link eventKey="posts">
                <Icon icon="mdi:post-outline" /> Posts
              </Nav.Link>
            </Nav.Item>
            <Nav.Item>
              <Nav.Link eventKey="insights">
                <Icon icon="mdi:chart-line" /> Analytics
              </Nav.Link>
            </Nav.Item>
            <Nav.Item>
              <Nav.Link eventKey="audience">
                <Icon icon="mdi:account-group-outline" /> Audience
              </Nav.Link>
            </Nav.Item>
            <Nav.Item>
              <Nav.Link eventKey="search">
                <Icon icon="mdi:magnify" /> Listening
              </Nav.Link>
            </Nav.Item>
          </Nav>

          <Tab.Content>
            {/* ------------------ PROFILE ------------------ */}
            <Tab.Pane eventKey="account">
              {!account ? (
                <div className="gmb-empty">
                  No Threads account is connected yet — use “Switch Account”
                  above. Threads uses its own login, so a Facebook Page isn't
                  needed.
                </div>
              ) : (
                <div className="gmb-card" style={{ padding: 20 }}>
                  <div className="gmb-detail-row">
                    <strong>Name</strong>
                    <span>{account.name || "-"}</span>
                  </div>
                  <div className="gmb-detail-row">
                    <strong>Handle</strong>
                    <span>@{account.username}</span>
                  </div>
                  <div className="gmb-detail-row">
                    <strong>Bio</strong>
                    <span>{account.biography || "-"}</span>
                  </div>
                  <div className="gmb-detail-row">
                    <strong>Followers</strong>
                    <span>{compact(account.followerCount)}</span>
                  </div>
                  <div className="gmb-detail-row">
                    <strong>Profile</strong>
                    <span>
                      <a
                        href={account.profileUrl}
                        target="_blank"
                        rel="noreferrer"
                      >
                        {account.profileUrl}
                      </a>
                    </span>
                  </div>
                  {quota && (
                    <>
                      <div className="gmb-detail-row">
                        <strong>Posts Today</strong>
                        <span>
                          {quota.postsUsed} of {quota.postsTotal} used
                        </span>
                      </div>
                      <div className="gmb-detail-row">
                        <strong>Replies Today</strong>
                        <span>
                          {quota.repliesUsed} of {quota.repliesTotal} used
                        </span>
                      </div>
                    </>
                  )}
                </div>
              )}
            </Tab.Pane>

            {/* ------------------ POSTS ------------------ */}
            <Tab.Pane eventKey="posts">
              <div className="d-flex justify-content-end mb-3">
                <button
                  className="btn btn-outline-primary-600 btn-sm"
                  onClick={loadPosts}
                >
                  <Icon icon="mdi:refresh" /> Refresh
                </button>
              </div>

              {posts.length === 0 ? (
                <div className="gmb-empty">No posts published yet.</div>
              ) : (
                <div className="row g-3">
                  {posts.map((p) => (
                    <div className="col-xl-4 col-lg-6" key={p.postId}>
                      <div className="gmb-card th-post-card">
                        <div className="th-post-head">
                          <img
                            className="th-avatar"
                            src={account?.profileImage || ""}
                            alt=""
                            onError={(e) =>
                              (e.currentTarget.style.visibility = "hidden")
                            }
                          />
                          <div>
                            <b>@{account?.username || "you"}</b>
                            <small>{fmtDate(p.timestamp)}</small>
                          </div>
                        </div>

                        {(p.isQuotePost ||
                          p.mediaType === "CAROUSEL_ALBUM" ||
                          p.mediaType === "VIDEO") && (
                          <div className="th-post-chips">
                            {p.isQuotePost && (
                              <span className="th-chip quote">Quote</span>
                            )}
                            {p.mediaType === "CAROUSEL_ALBUM" && (
                              <span className="th-chip carousel">Carousel</span>
                            )}
                            {p.mediaType === "VIDEO" && (
                              <span className="th-chip video">Video</span>
                            )}
                          </div>
                        )}

                        <div className="th-post-body">
                          {p.text || "(no text)"}
                        </div>

                        {p.thumbnailUrl && (
                          <img
                            className="th-post-media"
                            src={p.thumbnailUrl}
                            alt=""
                          />
                        )}

                        <div className="th-metric-row">
                          <span>
                            <Icon icon="mdi:heart-outline" />{" "}
                            {compact(p.likesCount)}
                          </span>
                          <span>
                            <Icon icon="mdi:comment-outline" />{" "}
                            {compact(p.repliesCount)}
                          </span>
                          <span>
                            <Icon icon="mdi:repeat-variant" />{" "}
                            {compact(p.repostsCount)}
                          </span>
                          <span className="th-views">
                            <Icon icon="mdi:chart-bar" />{" "}
                            {compact(p.viewsCount)}
                          </span>
                        </div>

                        <div className="d-flex gap-2 mt-3">
                          <button
                            className="btn btn-outline-primary-600 btn-sm"
                            onClick={() => openReplies(p)}
                          >
                            <Icon icon="mdi:comment-text-outline" /> Replies
                          </button>

                          <a
                            className="btn btn-outline-primary-600 btn-sm"
                            href={p.permalink}
                            target="_blank"
                            rel="noreferrer"
                          >
                            <Icon icon="mdi:open-in-new" />
                          </a>
                          <button
                            className="btn btn-outline-danger-600 btn-sm"
                            onClick={() => setConfirmDelete(p.postId)}
                          >
                            <Icon icon="fluent:delete-24-regular" />
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </Tab.Pane>

            {/* ------------------ ANALYTICS ------------------ */}
            <Tab.Pane eventKey="insights">
              <div className="gmb-date-row">
                <div>
                  <label>Start Date</label>
                  <input
                    type="date"
                    className="form-control"
                    value={range.start_date}
                    onChange={(e) =>
                      setRange((s) => ({ ...s, start_date: e.target.value }))
                    }
                  />
                </div>
                <div>
                  <label>End Date</label>
                  <input
                    type="date"
                    className="form-control"
                    value={range.end_date}
                    onChange={(e) =>
                      setRange((s) => ({ ...s, end_date: e.target.value }))
                    }
                  />
                </div>
                <button className="btn btn-primary-600" onClick={loadInsights}>
                  <Icon icon="mdi:magnify" /> Fetch
                </button>
              </div>

              <div className="gmb-tab-sub">
                Threads serves at most 30 days per request, and only from 13
                April 2024 onwards.
              </div>

              {!insights ? (
                <div className="gmb-empty">
                  Select a date range and click Fetch to load metrics.
                </div>
              ) : (
                <div className="row g-3">
                  {Object.entries(insights).map(([key, m]) => (
                    <div className="col-xl-3 col-lg-4 col-md-6" key={key}>
                      <div className="gmb-card gmb-metric-card">
                        <div
                          className="gmb-metric-icon"
                          style={{
                            background: "var(--neutral-100)",
                            color: "var(--th-ink)",
                          }}
                        >
                          <Icon
                            icon={
                              {
                                views: "mdi:chart-bar",
                                likes: "mdi:heart-outline",
                                replies: "mdi:comment-outline",
                                reposts: "mdi:repeat-variant",
                                quotes: "mdi:format-quote-close",
                                followers_count: "mdi:account-group-outline",
                              }[key] || "mdi:chart-box-outline"
                            }
                          />
                        </div>
                        <div className="gmb-metric-label">{m.title || key}</div>
                        <div className="gmb-metric-total gmb-mono">
                          {compact(m.total)}
                        </div>
                        <div className="gmb-metric-range">
                          <span>{range.start_date}</span>
                          <span>{range.end_date}</span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </Tab.Pane>

            {/* ------------------ AUDIENCE ------------------ */}
            <Tab.Pane eventKey="audience">
              <div className="gmb-tab-sub">
                Who follows your account on Threads.
              </div>

              {!demographics ? (
                <div className="gmb-empty">No demographics loaded yet.</div>
              ) : !demographics.hasData ? (
                <div className="gmb-empty">{demographics.note}</div>
              ) : (
                <div className="row g-3">
                  {DEMO_SECTIONS.map((sec) => {
                    const rows = demographics.demographics[sec.key] || [];
                    const max = Math.max(...rows.map((r) => r.value), 1);
                    return (
                      <div className="col-lg-6" key={sec.key}>
                        <div className="gmb-card th-demo-card">
                          <div className="th-demo-title">{sec.label}</div>
                          {rows.length === 0 ? (
                            <div className="gmb-char-count">
                              No data available.
                            </div>
                          ) : (
                            rows.map((r) => (
                              <div className="th-demo-row" key={r.label}>
                                <div className="th-demo-row-head">
                                  <span>{r.label}</span>
                                  <b>{compact(r.value)}</b>
                                </div>
                                <div className="th-demo-track">
                                  <span
                                    className="th-demo-fill"
                                    style={{
                                      width: `${(r.value / max) * 100}%`,
                                    }}
                                  />
                                </div>
                              </div>
                            ))
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </Tab.Pane>

            {/* ------------------ LISTENING ------------------ */}
            <Tab.Pane eventKey="search">
              <div className="th-search-bar">
                <div style={{ flex: 2 }}>
                  <label>Keyword</label>
                  <input
                    className="form-control"
                    placeholder="Eg: street food bhopal"
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                    onKeyDown={(e) => e.key === "Enter" && runSearch()}
                  />
                </div>
                <div>
                  <label>Sort</label>
                  <select
                    className="form-control"
                    value={searchType}
                    onChange={(e) => setSearchType(e.target.value)}
                  >
                    <option value="TOP">Top Posts</option>
                    <option value="RECENT">Most Recent</option>
                  </select>
                </div>
                <button className="btn btn-primary-600" onClick={runSearch}>
                  <Icon icon="mdi:magnify" /> Search
                </button>
              </div>

              {!searchResults ? (
                <div className="gmb-empty">
                  Search a keyword to see what people are posting in your niche.
                </div>
              ) : searchResults.results.length === 0 ? (
                <div className="gmb-empty">
                  No public posts found for “{searchResults.query}”.
                </div>
              ) : (
                <div className="d-flex flex-column gap-3">
                  {searchResults.results.map((r) => (
                    <div className="gmb-card th-post-card" key={r.postId}>
                      <div className="th-post-head">
                        <div className="fb-comment-avatar">
                          {initials(r.username)}
                        </div>
                        <div>
                          <b>@{r.username}</b>
                          <small>{fmtDate(r.timestamp)}</small>
                        </div>
                      </div>

                      <div className="th-post-body">{r.text}</div>

                      {r.thumbnailUrl && (
                        <img
                          className="th-post-media"
                          src={r.thumbnailUrl}
                          alt=""
                        />
                      )}

                      <div className="th-metric-row">
                        <a
                          className="gmb-competitor-maps-link ms-auto"
                          href={r.permalink}
                          target="_blank"
                          rel="noreferrer"
                        >
                          View on Threads
                        </a>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </Tab.Pane>
          </Tab.Content>
        </Tab.Container>

        {/* ==================== COMPOSER MODAL ==================== */}
        <Modal
          className="gmb-modal fb-composer-modal"
          size="xl"
          centered
          show={showComposer}
          onHide={() => setShowComposer(false)}
        >
          <Modal.Body>
            <div className="fb-composer">
              {/* ---------- LEFT: profiles + audience ---------- */}
              <div className="fb-composer-col fb-composer-rail">
                <div className="fb-composer-heading">
                  Select Social Profiles
                </div>

                {SOCIAL_PROFILES.map((sp) => {
                  const connected = sp.key === "threads" && !!account;
                  return (
                    <div
                      key={sp.key}
                      className={`fb-social-item ${sp.key} ${
                        connected ? "is-connected" : ""
                      }`}
                    >
                      <div
                        className="fb-social-head"
                        style={
                          sp.key === "threads"
                            ? { background: "var(--gmb-royal)" }
                            : undefined
                        }
                      >
                        <Icon icon={sp.icon} />
                        {sp.label}
                        {connected && (
                          <span className="fb-social-count">(1)</span>
                        )}
                      </div>
                      <div
                        className="fb-social-foot"
                        style={
                          sp.key === "threads"
                            ? {
                                background: "var(--neutral-100)",
                                color: "var(--gmb-ink-2)",
                              }
                            : undefined
                        }
                      >
                        <button
                          type="button"
                          disabled={!sp.ready}
                          onClick={sp.ready ? handleConnect : undefined}
                        >
                          {connected ? "Connected" : "Connect Account"}
                        </button>
                        <span className="fb-social-status">
                          <Icon
                            icon={
                              connected
                                ? "mdi:check-circle"
                                : "mdi:check-circle-outline"
                            }
                          />
                        </span>
                      </div>
                    </div>
                  );
                })}

                <div className="fb-rail-divider" />

                {quota && (
                  <>
                    <div className="th-quota">
                      <span className="th-quota-label">Posts</span>
                      <span className="th-quota-track">
                        <span
                          className="th-quota-fill"
                          style={{
                            width: `${Math.min(
                              (quota.postsUsed / quota.postsTotal) * 100,
                              100,
                            )}%`,
                          }}
                        />
                      </span>
                      <span className="th-quota-value">
                        {quota.postsRemaining} left
                      </span>
                    </div>
                    <div className="th-quota">
                      <span className="th-quota-label">Replies</span>
                      <span className="th-quota-track">
                        <span
                          className="th-quota-fill"
                          style={{
                            width: `${Math.min(
                              (quota.repliesUsed / quota.repliesTotal) * 100,
                              100,
                            )}%`,
                          }}
                        />
                      </span>
                      <span className="th-quota-value">
                        {quota.repliesRemaining} left
                      </span>
                    </div>
                    <div className="gmb-char-count">
                      Threads allows {quota.postsTotal} posts and{" "}
                      {quota.repliesTotal} replies per 24 hours.
                    </div>
                  </>
                )}

                <div className="fb-rail-divider" />

                <div className="fb-field-title">Who Can Reply</div>
                <div className="th-audience-list">
                  {REPLY_AUDIENCE.map((a) => {
                    const active = form.reply_control === a.key;
                    return (
                      <button
                        key={a.key}
                        type="button"
                        className={`th-audience-option ${active ? "active" : ""}`}
                        onClick={() =>
                          setForm((s) => ({ ...s, reply_control: a.key }))
                        }
                      >
                        <Icon icon={a.icon} className="th-audience-icon" />
                        <span>
                          <b>{a.label}</b>
                          <small>{a.hint}</small>
                        </span>
                        {active && (
                          <Icon
                            icon="mdi:check-circle"
                            className="th-audience-check"
                          />
                        )}
                      </button>
                    );
                  })}
                </div>

                <div className="fb-toggle-row mt-4">
                  <span>Scheduling</span>
                  <button type="button" className="fb-switch" disabled />
                </div>
                <div className="gmb-char-count">
                  Threads has no native scheduling — posts publish immediately.
                </div>
              </div>

              {/* ---------- MIDDLE: editor ---------- */}
              <div className="fb-composer-col">
                <div className="fb-composer-title">Create Post</div>
                <div className="fb-composer-sub">
                  {account
                    ? `@${account.username} selected`
                    : "No account selected"}
                </div>

                <div className="fb-channel-tabs">
                  {CHANNEL_TABS.map((c) => (
                    <button
                      key={c.key}
                      type="button"
                      disabled={!c.ready}
                      className={channel === c.key ? "active" : ""}
                      onClick={() => c.ready && setChannel(c.key)}
                    >
                      {c.label}
                    </button>
                  ))}
                </div>

                <div className="fb-account-row">
                  <Icon
                    icon="ri:threads-fill"
                    style={{ color: "var(--th-ink)" }}
                  />
                  Threads Account:
                  <span className="fb-account-chip">
                    {account ? `@${account.username}` : "Not connected"}
                  </span>
                </div>

                <div className="fb-field-title">Select Post Type</div>
                <div className="fb-pills">
                  {POST_TYPES.map((t) => (
                    <button
                      key={t.key}
                      type="button"
                      className={`fb-pill ${
                        form.post_type === t.key ? "active" : ""
                      }`}
                      onClick={() => {
                        setForm((s) => ({ ...s, post_type: t.key }));
                        setMedia([]);
                      }}
                    >
                      {form.post_type === t.key ? (
                        <Icon icon="mdi:check" />
                      ) : (
                        <Icon icon={t.icon} />
                      )}
                      {t.label}
                    </button>
                  ))}
                </div>

                {/* ---- Chain builder ---- */}
                {isChain ? (
                  <>
                    <div className="fb-field-title">
                      Chain ( each post replies to the one above )
                    </div>

                    {chainItems.map((item, i) => (
                      <div className="th-chain-item" key={i}>
                        <span className="th-chain-dot">{i + 1}</span>
                        <div className="th-chain-box">
                          <div className="th-chain-head">
                            <span className="th-chain-index">
                              Post {i + 1} of {chainItems.length}
                            </span>
                            <button
                              type="button"
                              className="th-chain-remove"
                              disabled={chainItems.length <= 2}
                              onClick={() =>
                                setChainItems((s) =>
                                  s.filter((_, idx) => idx !== i),
                                )
                              }
                            >
                              <Icon icon="mdi:close" />
                            </button>
                          </div>

                          <textarea
                            className="form-control"
                            rows={3}
                            placeholder={
                              i === 0
                                ? "Start with the hook..."
                                : "Continue the chain..."
                            }
                            value={item.text}
                            onChange={(e) =>
                              setChainItems((s) =>
                                s.map((it, idx) =>
                                  idx === i
                                    ? { ...it, text: e.target.value }
                                    : it,
                                ),
                              )
                            }
                          />

                          <CharBar count={item.text.length} />

                          <div className="th-media-strip">
                            {item.media ? (
                              <div className="th-media-item">
                                {item.media.kind === "VIDEO" ? (
                                  <video src={item.media.preview} muted />
                                ) : (
                                  <img src={item.media.preview} alt="" />
                                )}
                                <button
                                  type="button"
                                  onClick={() =>
                                    setChainItems((s) =>
                                      s.map((it, idx) =>
                                        idx === i ? { ...it, media: null } : it,
                                      ),
                                    )
                                  }
                                >
                                  <Icon icon="mdi:close" />
                                </button>
                              </div>
                            ) : (
                              <label className="th-media-add">
                                <input
                                  type="file"
                                  accept="image/*,video/*"
                                  onChange={(e) =>
                                    onPickChainMedia(i, e.target.files[0])
                                  }
                                />
                                <Icon icon="mdi:plus" />
                              </label>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}

                    <button
                      type="button"
                      className="th-chain-add"
                      disabled={chainItems.length >= 20}
                      onClick={() =>
                        setChainItems((s) => [...s, { text: "", media: null }])
                      }
                    >
                      <Icon icon="mdi:plus" /> Add Post
                    </button>
                  </>
                ) : (
                  <>
                    {/* ---- Media ---- */}
                    {isMedia && (
                      <>
                        <div className="fb-field-title">
                          {isCarousel
                            ? "Carousel Items ( 2 - 20 )"
                            : `Add ${form.post_type === "VIDEO" ? "Video" : "Image"}`}
                        </div>

                        {isCarousel ? (
                          <div className="th-media-strip">
                            {media.map((m, i) => (
                              <div className="th-media-item" key={m.url}>
                                {m.kind === "VIDEO" ? (
                                  <video src={m.preview} muted />
                                ) : (
                                  <img src={m.preview} alt="" />
                                )}
                                <button
                                  type="button"
                                  onClick={() =>
                                    setMedia((s) => s.filter((_, x) => x !== i))
                                  }
                                >
                                  <Icon icon="mdi:close" />
                                </button>
                              </div>
                            ))}
                            {media.length < 20 && (
                              <label className="th-media-add">
                                <input
                                  type="file"
                                  accept="image/*,video/*"
                                  onChange={(e) =>
                                    onPickMedia(e.target.files[0])
                                  }
                                />
                                <Icon icon="mdi:plus" />
                              </label>
                            )}
                          </div>
                        ) : media[0] ? (
                          <div className="fb-drop-preview">
                            {media[0].kind === "VIDEO" ? (
                              <video src={media[0].preview} controls />
                            ) : (
                              <img src={media[0].preview} alt="preview" />
                            )}
                            <button
                              type="button"
                              className="fb-drop-remove"
                              onClick={() => setMedia([])}
                            >
                              <Icon icon="mdi:close" />
                            </button>
                          </div>
                        ) : (
                          <label className="fb-dropzone">
                            <input
                              type="file"
                              accept={
                                form.post_type === "VIDEO"
                                  ? "video/*"
                                  : "image/*"
                              }
                              onChange={(e) => onPickMedia(e.target.files[0])}
                            />
                            <div className="fb-drop-icon">
                              <Icon icon="mdi:image-plus-outline" />
                            </div>
                            <div className="fb-drop-title">
                              Upload or Drag{" "}
                              {form.post_type === "VIDEO" ? "Video" : "Photo"}
                            </div>
                            <div className="fb-drop-hint">
                              Images up to 8 MB, video up to 1 GB and 5 minutes
                            </div>
                          </label>
                        )}

                        {isCarousel && (
                          <div className="gmb-char-count mt-2">
                            {media.length} of 20 items added
                          </div>
                        )}

                        {!isCarousel && media[0] && (
                          <div className="mt-3">
                            <div className="fb-field-title">
                              Alt Text (optional)
                            </div>
                            <input
                              className="form-control"
                              placeholder="Describe the media for screen readers"
                              value={form.alt_text}
                              onChange={(e) =>
                                setForm((s) => ({
                                  ...s,
                                  alt_text: e.target.value,
                                }))
                              }
                            />
                          </div>
                        )}
                      </>
                    )}

                    {form.post_type === "TEXT" && (
                      <div className="mb-3">
                        <div className="fb-field-title">
                          Link Attachment (optional)
                        </div>
                        <input
                          className="form-control"
                          placeholder="https://example.com/article"
                          value={form.link_attachment}
                          onChange={(e) =>
                            setForm((s) => ({
                              ...s,
                              link_attachment: e.target.value,
                            }))
                          }
                        />
                      </div>
                    )}

                    <div className="fb-field-title mt-3">Write a Post</div>
                    <textarea
                      className="form-control"
                      rows={5}
                      placeholder="Eg: We shipped the thing everyone kept asking for. Here's what changed..."
                      value={form.text}
                      onChange={(e) =>
                        setForm((s) => ({ ...s, text: e.target.value }))
                      }
                    />
                    <CharBar count={mainCount} />
                  </>
                )}

                <div className="fb-submit-bar">
                  <button
                    type="button"
                    className="btn btn-outline-primary-600"
                    onClick={() => setShowComposer(false)}
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    className="fb-submit"
                    disabled={!account}
                    onClick={publish}
                  >
                    <Icon icon="ri:threads-fill" />
                    {isChain ? "Publish Chain" : "Publish To Threads"}
                  </button>
                </div>
              </div>

              {/* ---------- RIGHT: preview + tips ---------- */}
              <div className="fb-composer-col fb-composer-aside">
                <div className="fb-preview">
                  <div className="fb-preview-head">
                    <Icon
                      icon="ri:threads-fill"
                      style={{ color: "var(--th-ink)" }}
                    />
                    Threads Preview
                  </div>

                  {previewItems.length === 0 ? (
                    <div className="fb-preview-empty">
                      <Icon
                        icon="mdi:text-box-outline"
                        style={{ fontSize: 34 }}
                      />
                      Nothing to preview yet
                    </div>
                  ) : (
                    previewItems.map((item, i) => (
                      <div className="th-preview-post" key={i}>
                        <div className="th-preview-line">
                          <img
                            className="th-avatar"
                            src={account?.profileImage || ""}
                            alt=""
                            onError={(e) =>
                              (e.currentTarget.style.visibility = "hidden")
                            }
                          />
                        </div>
                        <div className="th-preview-content">
                          <b>{account?.username || "yourbrand"}</b>
                          <p>{item.text || "Your content here"}</p>

                          {item.media &&
                            (item.media.kind === "VIDEO" ? (
                              <video src={item.media.preview} muted />
                            ) : (
                              <img src={item.media.preview} alt="" />
                            ))}

                          {!isChain && isCarousel && media[0] && (
                            <img src={media[0].preview} alt="" />
                          )}

                          {!isChain && form.link_attachment && (
                            <div className="th-preview-link">
                              {form.link_attachment}
                            </div>
                          )}

                          <div className="th-preview-actions">
                            <Icon icon="mdi:heart-outline" />
                            <Icon icon="mdi:comment-outline" />
                            <Icon icon="mdi:repeat-variant" />
                            <Icon icon="mdi:send-outline" />
                          </div>
                        </div>
                      </div>
                    ))
                  )}
                </div>

                <div className="fb-composer-heading">Tips to Improve Reach</div>
                {TIPS.map((t) => (
                  <div className="fb-tip-card" key={t.title}>
                    <div className="fb-tip-icon">
                      <Icon icon="mdi:lightbulb-on-outline" />
                    </div>
                    <h6>{t.title}</h6>
                    <p>{t.body}</p>
                  </div>
                ))}
              </div>
            </div>
          </Modal.Body>
        </Modal>

        {/* ==================== REPLIES MODAL ==================== */}
        <Modal
          className="gmb-modal"
          size="lg"
          centered
          show={showReplies}
          onHide={() => setShowReplies(false)}
        >
          <Modal.Header closeButton className="border-0 pb-0">
            <Modal.Title>Replies</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            {replyPost && (
              <div className="gmb-card th-post-card mb-3">
                <div className="th-post-body">{replyPost.text}</div>
                <div className="th-metric-row">
                  <span>
                    <Icon icon="mdi:heart-outline" />{" "}
                    {compact(replyPost.likesCount)}
                  </span>
                  <span>
                    <Icon icon="mdi:comment-outline" />{" "}
                    {compact(replyPost.repliesCount)}
                  </span>
                  <span className="th-views">
                    <Icon icon="mdi:chart-bar" />{" "}
                    {compact(replyPost.viewsCount)}
                  </span>
                </div>
              </div>
            )}

            <div className="d-flex gap-2 mb-3">
              <input
                className="form-control"
                placeholder="Write a reply..."
                maxLength={MAX_CHARS}
                value={replyBox[replyPost?.postId] || ""}
                onChange={(e) =>
                  setReplyBox((s) => ({
                    ...s,
                    [replyPost.postId]: e.target.value,
                  }))
                }
              />
              <button
                className="btn btn-primary-600"
                onClick={() => sendReply(replyPost.postId)}
              >
                Reply
              </button>
            </div>

            {replies.length === 0 ? (
              <div className="gmb-empty">No replies yet.</div>
            ) : (
              replies.map((r) => (
                <div
                  className={`th-reply ${
                    r.hideStatus === "HIDDEN" ? "hidden-reply" : ""
                  }`}
                  key={r.replyId}
                >
                  <div className="fb-comment-avatar">
                    {initials(r.username)}
                  </div>
                  <div className="th-reply-body">
                    <b>@{r.username}</b>
                    {r.isOwn && <span className="th-reply-own">You</span>}
                    <p>{r.text}</p>

                    {r.mediaUrl && (
                      <img
                        className="th-post-media mt-2"
                        src={r.mediaUrl}
                        alt=""
                        style={{ maxHeight: 140 }}
                      />
                    )}

                    <div className="fb-comment-actions">
                      <button
                        onClick={() =>
                          setReplyBox((s) => ({
                            ...s,
                            [r.replyId]: s[r.replyId] || "",
                          }))
                        }
                      >
                        Reply
                      </button>
                      {!r.isOwn && (
                        <button onClick={() => toggleHide(r)}>
                          {r.hideStatus === "HIDDEN" ? "Unhide" : "Hide"}
                        </button>
                      )}
                      {r.isOwn && (
                        <button
                          className="danger"
                          onClick={() => setConfirmDelete(r.replyId)}
                        >
                          Delete
                        </button>
                      )}
                      <span style={{ color: "var(--gmb-ink-3)" }}>
                        {fmtDate(r.timestamp)}
                      </span>
                    </div>

                    {replyBox[r.replyId] !== undefined && (
                      <div className="d-flex gap-2 mt-2">
                        <input
                          className="form-control"
                          placeholder="Write a reply..."
                          maxLength={MAX_CHARS}
                          value={replyBox[r.replyId]}
                          onChange={(e) =>
                            setReplyBox((s) => ({
                              ...s,
                              [r.replyId]: e.target.value,
                            }))
                          }
                        />
                        <button
                          className="btn btn-primary-600 btn-sm"
                          onClick={() => sendReply(r.replyId)}
                        >
                          Send
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              ))
            )}
          </Modal.Body>
        </Modal>

        {/* ==================== DELETE CONFIRM ==================== */}
        <Modal
          className="gmb-modal"
          size="sm"
          centered
          show={!!confirmDelete}
          onHide={() => setConfirmDelete(null)}
        >
          <Modal.Body className="text-center">
            <Icon
              icon="fluent:delete-24-regular"
              style={{ fontSize: 34, color: "var(--danger-main)" }}
            />
            <p className="mt-2 mb-0">
              Please confirm, do you want to delete this post?
            </p>
          </Modal.Body>
          <Modal.Footer>
            <button
              className="btn btn-outline-primary-600"
              onClick={() => setConfirmDelete(null)}
            >
              Cancel
            </button>
            <button className="btn btn-danger-600" onClick={removePost}>
              Confirm
            </button>
          </Modal.Footer>
        </Modal>
      </div>
    </MasterLayout>
  );
}

export default ThreadsPanel;
